import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Box, Button, SxProps, Theme, Typography } from "@mui/material";
import { TypographyVariant } from "@mui/material/styles";

/** Lines shown before the text is clamped. */
const DEFAULT_LINES = 4;

/**
 * Long read-only text, clamped until the reader asks for the rest.
 *
 * ⚠️ Clamped with `-webkit-line-clamp`, not MUI's `Collapse`. `Collapse` takes a
 * fixed `collapsedSize`, so a two-line value gets padded out to the collapsed
 * height and nothing marks the cut; the clamp collapses to the content's own
 * height and draws the ellipsis itself.
 *
 * Renders text and a toggle, and nothing else: no border, no padding, no
 * background. Chrome is the caller's, and callers differ — the accept modal wants
 * none, `TextToggledField` wraps it in a labelled panel. Keeping it bare is what
 * lets both hold without either overriding the other.
 */
const TextToggled = ({
  text,
  lines = DEFAULT_LINES,
  variant = "body2",
  sx,
}: {
  text: string;
  lines?: number;
  variant?: TypographyVariant;
  sx?: SxProps<Theme>;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  // The node in state rather than a ref: a ref gives no signal when it attaches,
  // so an effect keyed on it would measure nothing on the first render.
  const [node, setNode] = useState<HTMLElement | null>(null);

  // ⚠️ Collapse again when the text changes — a second event opened in the same
  // modal would otherwise inherit the first one's expansion. Adjusted during
  // render rather than in an effect, the same shape `CustomSelect` uses for
  // `defaultValue`, so the new text never paints expanded first.
  const [lastText, setLastText] = useState(text);
  if (text !== lastText) {
    setLastText(text);
    setExpanded(false);
  }

  useEffect(() => {
    // ⚠️ Measured only while clamped. Expanded, `scrollHeight === clientHeight`,
    // so re-measuring would report "it fits", hide the control, and strand the
    // reader with no way back.
    if (!node || expanded) return;

    const measure = () => {
      const next = node.scrollHeight > node.clientHeight;
      // Bail when unchanged: the observer fires on every layout pass, and this
      // component's own render changes layout.
      setOverflows((previous) => (previous === next ? previous : next));
    };

    // ⚠️ Measured once here, not only from the observer. Two texts that both
    // clamp to the same number of lines occupy the same box, so swapping one for
    // the other fires no resize event — and a 10-line description replaced by an
    // exactly-4-line one would keep a "See more" that does nothing.
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, expanded, text]);

  return (
    <Box
      onClick={() => overflows && !expanded && setExpanded(true)}
      sx={{ cursor: overflows && !expanded ? "pointer" : "default" }}
    >
      <Typography
        ref={setNode}
        variant={variant}
        sx={[
          ...(Array.isArray(sx) ? sx : [sx]),
          ...(expanded
            ? []
            : [
                {
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: lines,
                  overflow: "hidden",
                },
              ]),
        ]}
      >
        {text}
      </Typography>
      {overflows && (
        <Button
          size="small"
          // The box expands on click already; this is the keyboard-reachable
          // control and the only way back. `stopPropagation` so collapsing is not
          // immediately undone by the box's own handler.
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((previous) => !previous);
          }}
          sx={{ mt: 0.5, p: 0, minWidth: 0 }}
        >
          <FormattedMessage id={expanded ? "SEE_LESS" : "SEE_MORE"} />
        </Button>
      )}
    </Box>
  );
};

export default TextToggled;
